/**
 * Settings, On call: the people notifications go to (their quiet hours, floor and rules),
 * the rotas that say who is on duty for a project, and what is escalating right now.
 */
import { ArrowDown, ArrowUp, CalendarClock, Siren, Moon, Pencil, Plus, Trash2, UserPlus, Users, X } from '@orgus/ui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type { EscalationView, NotifyMember, NotifyMemberInput, NotifyView, Project, RotaRotation, RotaView, RoutingRule, Urgency } from '@orgus/protocol';
import {
  Avatar,
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  Panel,
  RelativeTime,
  Segmented,
  Select,
  SkeletonRows,
  toast,
  toUiError,
} from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { useCan, useMe } from '../auth/useMe.ts';
import { ackEscalation, addMember, addRota, deleteMember, deleteRota, getNotify, importMembers, patchMember, patchRota, useNotifyState } from './api.ts';
import { DEMO_MEMBERS, demoNotifyState, demoRotas } from './demo.ts';
import { KIND_OPTIONS, RULE_ACTION_OPTIONS, URGENCY_FLOOR_OPTIONS } from './kinds.ts';

const ESCALATE_OPTIONS = [
  { value: 'never', label: 'Never' },
  ...[5, 10, 15, 30, 60].map((m) => ({ value: String(m), label: `After ${m} min` })),
];

const ROTATION_LABEL: Record<RotaRotation, string> = { manual: 'Manual', daily: 'Daily', weekly: 'Weekly' };

function useProjects(): Project[] {
  const projects = useOrgus((s) => s.projects);
  return useMemo(() => Object.values(projects).sort((a, b) => a.name.localeCompare(b.name)), [projects]);
}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

function quietLabel(m: NotifyMember): string {
  return m.quietHours ? `Quiet ${m.quietHours.from} to ${m.quietHours.to}` : 'No quiet hours of their own';
}

/** `selfOnly`: a person editing their own entry, who may not change who they are (name, login, Slack ID). */
function MemberDialog({ open, onOpenChange, member, selfOnly, onSaved }: { open: boolean; onOpenChange: (o: boolean) => void; member: NotifyMember | null; selfOnly: boolean; onSaved: () => void }) {
  const projects = useProjects();
  const blank: NotifyMemberInput = { name: '', login: '', slackUserId: '', quietHours: null, minUrgency: 'normal', dailySummary: true, rules: [] };
  const [f, setF] = useState<NotifyMemberInput>(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ why: string; next: string } | null>(null);
  useEffect(() => {
    if (!open) return;
    setError(null);
    setF(member ? { name: member.name, login: member.login ?? '', slackUserId: member.slackUserId ?? '', quietHours: member.quietHours, minUrgency: member.minUrgency, dailySummary: member.dailySummary, rules: member.rules } : blank);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, member]);
  const rules = f.rules ?? [];
  const setRule = (i: number, patch: Partial<RoutingRule>) => setF({ ...f, rules: rules.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const full: NotifyMemberInput = { ...f, login: f.login?.trim() || null, slackUserId: f.slackUserId?.trim() || null };
    // The hub refuses identity fields from someone editing themselves, so they are left out.
    const { name: _name, login: _login, slackUserId: _slack, ...own } = full;
    const body: NotifyMemberInput = selfOnly && member ? own : full;
    try {
      if (member) await patchMember(member.id, body);
      else await addMember(body);
      toast.success(member ? 'Person saved' : 'Person added', full.name ?? undefined);
      onOpenChange(false);
      onSaved();
    } catch (err) {
      const u = toUiError(err);
      setError({ why: u.why || 'The hub did not say why.', next: u.next || 'Check the fields above and save again.' });
    } finally {
      setBusy(false);
    }
  };
  const projectOptions = [{ value: '', label: 'Any project' }, ...projects.map((p) => ({ value: p.id, label: p.name }))];
  const kindOptions = [{ value: '', label: 'Anything' }, ...KIND_OPTIONS];
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={member ? `Edit ${member.name}` : 'Add a person'}
      description="Critical always gets through, even in quiet hours."
      dismissible={!busy}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="member-form" loading={busy}>
            {member ? 'Save person' : 'Add person'}
          </Button>
        </>
      }
    >
      <form id="member-form" onSubmit={submit} className="flex flex-col gap-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Input label="Name" required disabled={selfOnly} value={f.name ?? ''} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ana Ruiz" />
          <Input label="Sign-in login" disabled={selfOnly} value={f.login ?? ''} onChange={(e) => setF({ ...f, login: e.target.value })} placeholder="ana" hint={selfOnly ? 'An admin changes this.' : 'Their devices and reads.'} spellCheck={false} />
          <Input label="Slack member ID" disabled={selfOnly} value={f.slackUserId ?? ''} onChange={(e) => setF({ ...f, slackUserId: e.target.value })} placeholder="U04ANA1" hint={selfOnly ? 'An admin changes this.' : 'Who pressed a Slack button.'} spellCheck={false} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <p className="text-caption font-medium text-fg-muted">Quiet hours</p>
            <Segmented<'off' | 'on'>
              label="Quiet hours"
              className="self-start"
              value={f.quietHours ? 'on' : 'off'}
              onChange={(v) => setF({ ...f, quietHours: v === 'on' ? (f.quietHours ?? { from: '22:00', to: '07:00' }) : null })}
              options={[
                { value: 'off', label: 'Hub default' },
                { value: 'on', label: 'Their own' },
              ]}
            />
            {f.quietHours && (
              <div className="flex items-end gap-2">
                <Input label="From" type="time" value={f.quietHours.from} onChange={(e) => setF({ ...f, quietHours: { ...f.quietHours!, from: e.target.value } })} />
                <Input label="To" type="time" value={f.quietHours.to} onChange={(e) => setF({ ...f, quietHours: { ...f.quietHours!, to: e.target.value } })} />
              </div>
            )}
          </div>
          <div className="flex flex-col gap-3">
            <Select label="Sends them" value={f.minUrgency ?? 'normal'} options={URGENCY_FLOOR_OPTIONS} onValueChange={(v) => setF({ ...f, minUrgency: v as Urgency })} hint="When they are on duty, high gets through anyway." />
            <div className="flex flex-col gap-2">
              <p className="text-caption font-medium text-fg-muted">Daily summary</p>
              <Segmented<'on' | 'off'>
                label="Daily summary"
                className="self-start"
                value={f.dailySummary === false ? 'off' : 'on'}
                onChange={(v) => setF({ ...f, dailySummary: v === 'on' })}
                options={[
                  { value: 'on', label: 'On' },
                  { value: 'off', label: 'Off' },
                ]}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <p className="text-caption font-medium text-fg-muted">Rules, first match wins</p>
            <Button size="sm" variant="ghost" icon={<Plus />} onClick={() => setF({ ...f, rules: [...rules, { id: `new_${rules.length}_${Date.now()}`, projectId: null, kind: null, action: 'always' }] })}>
              Add rule
            </Button>
          </div>
          {rules.length ? (
            <ul className="flex flex-col gap-2">
              {rules.map((r, i) => (
                <li key={r.id} className="flex flex-wrap items-end gap-2 rounded-control border border-border-subtle p-2">
                  <Select size="sm" label="Project" wrapperClassName="min-w-40 flex-1" value={r.projectId ?? ''} options={projectOptions} onValueChange={(v) => setRule(i, { projectId: v || null })} />
                  <Select size="sm" label="Kind" wrapperClassName="min-w-44 flex-1" value={r.kind ?? ''} options={kindOptions} onValueChange={(v) => setRule(i, { kind: v || null })} />
                  <Select size="sm" label="Then" wrapperClassName="min-w-52 flex-1" value={r.action} options={RULE_ACTION_OPTIONS} onValueChange={(v) => setRule(i, { action: v as RoutingRule['action'] })} />
                  <IconButton label="Remove rule" icon={<X />} onClick={() => setF({ ...f, rules: rules.filter((_, j) => j !== i) })} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-caption text-fg-subtle">No rules: their floor and quiet hours decide.</p>
          )}
        </div>
        {error && <ErrorState variant="inline" error={{ what: 'The person was not saved.', why: error.why, next: error.next }} />}
      </form>
    </Dialog>
  );
}

/** `readOnly`: no adding, importing or removing (demo data, or not an admin). `selfLogin`: that person may still edit their own entry. */
function People({ members, readOnly, canImport, selfLogin, onChanged }: { members: NotifyMember[]; readOnly: boolean; canImport: boolean; selfLogin: string | null; onChanged: () => void }) {
  const [editing, setEditing] = useState<NotifyMember | null>(null);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<NotifyMember | null>(null);
  const [importing, setImporting] = useState(false);
  const isSelf = (m: NotifyMember) => !!selfLogin && m.login?.toLowerCase() === selfLogin.toLowerCase();
  const runImport = async () => {
    setImporting(true);
    try {
      const r = await importMembers();
      toast.success(r.added ? `Added ${r.added} ${r.added === 1 ? 'person' : 'people'}` : 'Everyone is already here');
      onChanged();
    } catch (e) {
      toast.error(e);
    } finally {
      setImporting(false);
    }
  };
  return (
    <Panel
      title="People"
      sub={members.length}
      icon={<Users />}
      flush
      actions={
        readOnly ? undefined : (
          <div className="flex items-center gap-1">
            {canImport && (
              <Button size="sm" variant="ghost" loading={importing} onClick={() => void runImport()}>
                Import members
              </Button>
            )}
            <Button size="sm" variant="secondary" icon={<UserPlus />} onClick={() => setAdding(true)}>
              Add person
            </Button>
          </div>
        )
      }
    >
      {members.length ? (
        <ul className="divide-y divide-border-subtle">
          {members.map((m) => (
            <li key={m.id} className="flex items-center gap-3 px-4 py-3">
              <Avatar name={m.name} size={28} />
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 text-ui font-medium text-fg">
                  <span className="truncate">{m.name}</span>
                  {m.login && <span className="truncate text-caption font-normal text-fg-subtle">{m.login}</span>}
                </p>
                <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-fg-subtle">
                  <span className="flex items-center gap-1">
                    <Moon className="size-3.5" aria-hidden />
                    {quietLabel(m)}
                  </span>
                  <span>{URGENCY_FLOOR_OPTIONS.find((o) => o.value === m.minUrgency)?.label}</span>
                  {m.rules.length > 0 && <span>{m.rules.length === 1 ? '1 rule' : `${m.rules.length} rules`}</span>}
                  {m.slackUserId ? <Badge>Slack linked</Badge> : null}
                  {!m.dailySummary && <Badge tone="idle">No summary</Badge>}
                </p>
              </div>
              {(!readOnly || isSelf(m)) && (
                <div className="flex items-center gap-1">
                  <IconButton label={`Edit ${m.name}`} icon={<Pencil />} onClick={() => setEditing(m)} />
                  {!readOnly && <IconButton label={`Remove ${m.name}`} variant="danger" icon={<Trash2 />} onClick={() => setRemoving(m)} />}
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState
          icon={<Users />}
          title="No people yet"
          description="Add people with their own quiet hours and rules, then put them on a rota."
          action={
            readOnly ? undefined : (
              <Button variant="secondary" icon={<UserPlus />} onClick={() => setAdding(true)}>
                Add person
              </Button>
            )
          }
        />
      )}
      <MemberDialog open={adding || !!editing} onOpenChange={(o) => (o ? undefined : (setAdding(false), setEditing(null)))} member={editing} selfOnly={readOnly} onSaved={onChanged} />
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => (o ? undefined : setRemoving(null))}
        title={`Remove ${removing?.name ?? 'this person'}?`}
        description="They come off every rota and their channels become shared."
        confirmLabel="Remove person"
        onConfirm={async () => {
          if (removing) await deleteMember(removing.id);
          onChanged();
        }}
      />
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Rotas
// ---------------------------------------------------------------------------

/** `readOnly`: the rota cannot be changed (demo data, or not an admin). `canHandOver`: operators may still put someone on duty. */
function RotaCard({ r, members, readOnly, canHandOver, onChanged }: { r: RotaView; members: NotifyMember[]; readOnly: boolean; canHandOver: boolean; onChanged: () => void }) {
  const projects = useProjects();
  const [busy, setBusy] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const byId = new Map(members.map((m) => [m.id, m]));
  const save = async (what: string, patch: Parameters<typeof patchRota>[1], ok?: string) => {
    setBusy(what);
    try {
      await patchRota(r.id, patch);
      if (ok) toast.success(ok, r.name);
      onChanged();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };
  const move = (i: number, d: -1 | 1) => {
    const ids = [...r.memberIds];
    const j = i + d;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    void save('order', { memberIds: ids });
  };
  const notOn = members.filter((m) => !r.memberIds.includes(m.id));
  const project = r.projectId ? (projects.find((p) => p.id === r.projectId)?.name ?? 'A removed project') : 'All projects without their own rota';
  return (
    <li className="flex flex-col gap-3 px-4 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <CalendarClock className="size-4 text-fg-subtle" aria-hidden />
        <p className="text-ui font-semibold text-fg">{r.name}</p>
        <Badge variant="outline">{project}</Badge>
        <Badge>{ROTATION_LABEL[r.rotation]}</Badge>
        {r.onDutyOnly && <Badge tone="accent">Pages the person on duty only</Badge>}
        <span className="flex-1" />
        {!readOnly && <IconButton label={`Remove ${r.name}`} variant="danger" icon={<Trash2 />} onClick={() => setRemoving(true)} />}
      </div>
      {r.memberIds.length ? (
        <ol className="flex flex-col divide-y divide-border-subtle rounded-control border border-border">
          {r.memberIds.map((id, i) => {
            const m = byId.get(id);
            const duty = id === r.onDuty;
            return (
              <li key={id} className="flex items-center gap-3 px-3 py-2">
                <span className="num w-5 text-caption text-fg-subtle">{i + 1}</span>
                <Avatar name={m?.name ?? '?'} size={22} />
                <span className="min-w-0 flex-1 truncate text-ui text-fg">{m?.name ?? 'A removed person'}</span>
                {duty ? (
                  <Badge tone="passed" dot>
                    On duty
                  </Badge>
                ) : (
                  canHandOver && (
                    <Button size="sm" variant="ghost" loading={busy === `duty-${id}`} onClick={() => void save(`duty-${id}`, { onDuty: id }, `${m?.name ?? 'They'} is on duty`)}>
                      Put on duty
                    </Button>
                  )
                )}
                {!readOnly && (
                  <div className="flex items-center">
                    <IconButton size="xs" label="Move up" icon={<ArrowUp />} disabled={i === 0} onClick={() => move(i, -1)} />
                    <IconButton size="xs" label="Move down" icon={<ArrowDown />} disabled={i === r.memberIds.length - 1} onClick={() => move(i, 1)} />
                    <IconButton size="xs" label={`Take ${m?.name ?? 'them'} off the rota`} icon={<X />} onClick={() => void save('remove', { memberIds: r.memberIds.filter((x) => x !== id) })} />
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="text-caption text-fg-subtle">Nobody is on this rota yet, so nothing escalates through it.</p>
      )}
      <div className="flex flex-wrap items-end gap-3">
        {!readOnly && notOn.length > 0 && (
          <Select
            size="sm"
            label="Add to the rota"
            wrapperClassName="w-48"
            value=""
            placeholder="Pick a person"
            options={notOn.map((m) => ({ value: m.id, label: m.name }))}
            onValueChange={(v) => v && void save('add', { memberIds: [...r.memberIds, v] })}
          />
        )}
        <Select
          size="sm"
          label="Escalate to the next person"
          wrapperClassName="w-48"
          disabled={readOnly}
          value={r.escalateAfterMin ? String(r.escalateAfterMin) : 'never'}
          options={ESCALATE_OPTIONS}
          onValueChange={(v) => void save('esc', { escalateAfterMin: v === 'never' ? null : Number(v) })}
        />
        <div className="flex flex-col gap-2">
          <span className="text-caption font-medium text-fg-muted">Hands over</span>
          <Segmented<RotaRotation>
            label="Hands over"
            value={r.rotation}
            onChange={(v) => !readOnly && void save('rotation', { rotation: v })}
            options={[
              { value: 'manual', label: 'Manual' },
              { value: 'daily', label: 'Daily' },
              { value: 'weekly', label: 'Weekly' },
            ]}
          />
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-caption font-medium text-fg-muted">Who is paged</span>
          <Segmented<'duty' | 'all'>
            label="Who is paged"
            value={r.onDutyOnly ? 'duty' : 'all'}
            onChange={(v) => !readOnly && void save('only', { onDutyOnly: v === 'duty' })}
            options={[
              { value: 'all', label: 'Everyone' },
              { value: 'duty', label: 'On duty only' },
            ]}
          />
        </div>
      </div>
      {r.nextHandoffAt && (
        <p className="text-caption text-fg-subtle">
          Next handover <RelativeTime ts={r.nextHandoffAt} /> to {byId.get(r.memberIds[(r.memberIds.indexOf(r.onDuty ?? '') + 1) % r.memberIds.length] ?? '')?.name ?? 'the next person'}.
        </p>
      )}
      <ConfirmDialog
        open={removing}
        onOpenChange={setRemoving}
        title={`Remove the rota "${r.name}"?`}
        description="Items for its project stop escalating."
        confirmLabel="Remove rota"
        onConfirm={async () => {
          await deleteRota(r.id);
          onChanged();
        }}
      />
    </li>
  );
}

function AddRota({ rotas, onAdded }: { rotas: RotaView[]; onAdded: () => void }) {
  const projects = useProjects();
  const taken = new Set(rotas.map((r) => r.projectId ?? ''));
  const options = [{ value: '', label: 'All projects without their own rota' }, ...projects.map((p) => ({ value: p.id, label: p.name }))].filter((o) => !taken.has(o.value));
  const [name, setName] = useState('');
  const [projectId, setProjectId] = useState(options[0]?.value ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!options.some((o) => o.value === projectId)) setProjectId(options[0]?.value ?? '');
  }, [options, projectId]);
  if (!options.length) {
    return (
      <p className="border-t border-border-subtle px-4 py-3 text-caption text-fg-subtle">
        {projects.length ? 'Every project has a rota.' : 'Add a project to give it a rota of its own; this one covers everything until then.'}
      </p>
    );
  }
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await addRota({ name: name.trim() || 'On call', projectId: projectId || null, escalateAfterMin: 10, rotation: 'weekly' });
      setName('');
      toast.success('Rota added', 'Add people to it next.');
      onAdded();
    } catch (err) {
      const u = toUiError(err);
      setError(`${u.why} ${u.next}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-3 border-t border-border-subtle px-4 py-3">
      <Input label="New rota" placeholder="Platform on call" value={name} onChange={(e) => setName(e.target.value)} wrapperClassName="w-56" error={error ?? undefined} />
      <Select label="For" value={projectId} options={options} onValueChange={setProjectId} wrapperClassName="w-64" />
      <Button type="submit" variant="secondary" icon={<Plus />} loading={busy}>
        Add rota
      </Button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Escalating now
// ---------------------------------------------------------------------------

function Escalations({ list, members, readOnly }: { list: EscalationView[]; members: NotifyMember[]; readOnly: boolean }) {
  const [busy, setBusy] = useState<string | null>(null);
  const name = (id: string | null) => members.find((m) => m.id === id)?.name ?? 'nobody';
  return (
    <Panel title="Escalating now" sub={list.length} icon={<Siren />} flush>
      {list.length ? (
        <ul className="divide-y divide-border-subtle">
          {list.map((e) => (
            <li key={e.key} className="flex items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-ui text-fg">{e.title}</p>
                <p className="text-caption text-fg-subtle">
                  With {name(e.memberId)}, {e.step === 0 ? 'first on the rota' : `step ${e.step + 1}`}. Waiting since <RelativeTime ts={e.startedAt} />
                  {e.nextAt ? (
                    <>
                      , moves on <RelativeTime ts={e.nextAt} />
                    </>
                  ) : null}
                  .
                </p>
              </div>
              <Badge tone="waiting" dot>
                Escalating
              </Badge>
              {!readOnly && (
                <Button
                  size="sm"
                  variant="secondary"
                  loading={busy === e.key}
                  onClick={async () => {
                    setBusy(e.key);
                    try {
                      await ackEscalation(e.key);
                      toast.success('Acknowledged', e.title);
                    } catch (err) {
                      toast.error(err);
                    } finally {
                      setBusy(null);
                    }
                  }}
                >
                  Acknowledge
                </Button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState icon={<Siren />} title="Nothing is escalating" description="When a high-urgency item waits longer than its rota allows, it moves to the next person and shows here." />
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// The section
// ---------------------------------------------------------------------------

export function OnCallSettings() {
  const live = useNotifyState();
  const mode = useOrgus((s) => s.mode);
  const hasAuth = useOrgus((s) => !!s.ext?.auth);
  const canAdmin = useCan('admin');
  const canAct = useCan('operator');
  const selfLogin = useMe((s) => (s.me?.member ? s.me.actor.id : null));
  const [view, setView] = useState<NotifyView | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setView(await getNotify());
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mode === 'mock') {
      const now = Date.now();
      setView({ ...demoNotifyState(now), settings: { quietHours: null, digestAt: '18:00', triggers: {} as never, publicUrl: null }, digestQueued: 0, members: DEMO_MEMBERS, rotas: demoRotas(now) });
      setLoading(false);
      return;
    }
    void load();
  }, [load, mode]);

  if (loading && !view) return <SkeletonRows rows={6} />;
  if (error && !view) {
    if ((error as { status?: number }).status === 404) {
      return <EmptyState icon={<Users />} title="On call is not running on this hub" description="Update the hub to a version with the notify module, then reload this page." />;
    }
    return <ErrorState error={error} onRetry={load} />;
  }
  if (!view) return null;
  const readOnly = mode === 'mock';
  // The hub enforces these too; hiding them saves a 403. Admins manage, operators hand over and acknowledge.
  const manage = !readOnly && canAdmin;
  const act = !readOnly && canAct;
  const members = view.members ?? [];
  const rotas = view.rotas ?? [];
  const escalations = readOnly ? (view.escalations ?? []) : (live?.escalations ?? view.escalations ?? []);

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      {readOnly && (
        <p role="status" className="rounded-control border border-border bg-accent-soft px-3 py-2 text-caption text-fg-muted">
          Demo data: connect to a hub to set up people and rotas.
        </p>
      )}
      {!readOnly && !canAdmin && (
        <p role="status" className="rounded-control border border-border px-3 py-2 text-caption text-fg-muted">
          Only owners and admins add people and change rotas.{' '}
          {canAct ? 'You can put someone on duty, acknowledge escalations, and change your own quiet hours and rules.' : 'You can change your own quiet hours and rules.'}
        </p>
      )}
      <Escalations list={escalations} members={members} readOnly={!act} />
      <Panel title="Rotas" sub={rotas.length} icon={<CalendarClock />} flush>
        {rotas.length ? (
          <ul className="divide-y divide-border-subtle">
            {rotas.map((r) => (
              <RotaCard key={r.id} r={r} members={members} readOnly={!manage} canHandOver={act} onChanged={() => void load()} />
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<CalendarClock />}
            title="No rotas yet"
            description={members.length ? 'A rota says who is on duty for a project, and who is next when they do not answer.' : 'Add people first, then make a rota for a project or for everything.'}
          />
        )}
        {manage && members.length > 0 && <AddRota rotas={rotas} onAdded={() => void load()} />}
      </Panel>
      <People members={members} readOnly={!manage} canImport={hasAuth} selfLogin={readOnly ? null : selfLogin} onChanged={() => void load()} />
    </div>
  );
}
